import { CanActivateFn, CanMatchFn, Routes } from '@angular/router';
import { Licenses, StartPageVersions } from '@shared/constants';
import { Paths } from '@shared/constants/paths.constants';
import { License, StartPageVersion } from '@shared/types';
import { configCanActivateFn, configCanMatchFn } from './guards/config.guard';
import { licensesCanMatch } from './guards/licenses.guard';
import { concatCanActivate, concatCanMatch } from './guards/ordered-guards';
import { redirectCanActivateFn, redirectCanMatchFn } from './guards/redirect.guard';
import { serviceVersionCanActivateFn, serviceVersionCanMatchFn } from './guards/service-version.guard';
import { ssoAuthCanActivateFn, ssoAuthCanMatchFn } from './guards/sso-auth.guard';
import { startPageCanMatchFn } from './guards/start-page.guard';
import { BasicLayoutComponent } from './layout/basic-layout/basic-layout.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { ErrorPageComponent } from './pages/error-page/error-page.component';
import { InfoComponent } from './pages/info/info.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { StartPageCardsComponent } from './pages/start-page-cards/start-page-cards.component';
import { StartPageCircleComponent } from './pages/start-page-circle/start-page-circle.component';
import { i18nResolver } from './services/resolver/i18n.resolver';

const authAndConfigCanActivateFn: CanActivateFn = concatCanActivate(
    serviceVersionCanActivateFn,
    ssoAuthCanActivateFn,
    configCanActivateFn,
    redirectCanActivateFn,
);

const baseCanMatchFns = concatCanMatch(serviceVersionCanMatchFn, ssoAuthCanMatchFn, configCanMatchFn, redirectCanMatchFn);

const licensesCanMatchFns = (requiredLicenses: License | License[]): CanMatchFn =>
    concatCanMatch(baseCanMatchFns, licensesCanMatch(requiredLicenses));

const startPageCanMatchFns = (startPageVersion: StartPageVersion): CanMatchFn =>
    concatCanMatch(baseCanMatchFns, startPageCanMatchFn(startPageVersion));

export const routes: Routes = [
    {
        path: '',
        component: MainLayoutComponent,
        resolve: { resolvedI18nData: i18nResolver },
        canActivate: [authAndConfigCanActivateFn],
        children: [
            {
                // Redirects '/' to the configured landing page, also for in-app navigation where the parent guards are not re-run
                path: '',
                pathMatch: 'full',
                canActivate: [redirectCanActivateFn],
                children: [],
            },
            {
                path: Paths.Start,
                children: [
                    {
                        path: '',
                        title: 'Einsenderportal',
                        component: StartPageCircleComponent,
                        canMatch: [startPageCanMatchFns(StartPageVersions.Circle)],
                    },
                    {
                        path: '',
                        title: 'Startseite',
                        component: StartPageCardsComponent,
                        canMatch: [startPageCanMatchFns(StartPageVersions.Cards)],
                    },
                    {
                        path: '',
                        redirectTo: '/',
                        pathMatch: 'full',
                    },
                ],
            },
            {
                path: Paths.Reports,
                canMatch: [licensesCanMatchFns(Licenses.Reports)],
                loadChildren: () => import('./routes/reports/routes'),
            },
            {
                path: Paths.Orders,
                canMatch: [licensesCanMatchFns(Licenses.Orders)],
                loadChildren: () => import('./routes/orders/routes'),
            },
            {
                path: Paths.PatientsSearch,
                canMatch: [licensesCanMatchFns(Licenses.PatientsSearch)],
                loadChildren: () => import('./routes/patients-search/routes'),
            },
            {
                path: Paths.OrderCreation,
                canMatch: [licensesCanMatchFns(Licenses.OrderCreation)],
                loadChildren: () => import('./routes/order-creation/routes'),
            },
            {
                path: Paths.Documents,
                canMatch: [licensesCanMatchFns(Licenses.Documents)],
                loadChildren: () => import('./routes/documents-view/routes'),
            },
            {
                path: Paths.Shop,
                canMatch: [licensesCanMatchFns(Licenses.Shop)],
                loadChildren: () => import('./routes/shop/routes'),
            },
            {
                path: Paths.Info,
                title: 'Informationen',
                component: InfoComponent,
            },
        ],
    },
    {
        path: 'error',
        title: 'Fehler',
        component: BasicLayoutComponent,
        children: [{ path: '', component: ErrorPageComponent }],
    },
    {
        path: '**',
        title: 'Seite nicht gefunden',
        component: BasicLayoutComponent,
        children: [{ path: '', component: NotFoundComponent }],
    },
];
